import type { Dispatch, SetStateAction } from 'react'
import type { IPoiPendingImage } from '@/shared/api/types/poi-types'
import { useTranslation } from 'react-i18next'
import { uploadImageWithValidation } from '@/shared/lib/upload/upload-image-with-validation'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'

/**
 * Uploads the files picked in the image field.
 *
 * VI: Mỗi file hiện ngay bằng blob preview với trạng thái `uploading`, upload tuần tự, rồi chuyển `success` (thay preview bằng URL thật và giải phóng blob) hoặc `error` (kèm thông báo để user xử lý trước khi lưu). | EN: Each file shows at once as a blob preview in `uploading` state and uploads one after another, then becomes `success` (real URL replaces the preview and the blob is released) or `error` (with a message to resolve before saving).
 */
export function usePoiImageUpload(setImages: Dispatch<SetStateAction<IPoiPendingImage[]>>) {
  const { t } = useTranslation()

  async function handleSelectedImages(files: File[]) {
    for (const file of files) {
      const previewUrl = URL.createObjectURL(file)
      const localId = `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`

      setImages((prev) => [...prev, { id: localId, file, previewUrl, status: 'uploading' }])

      try {
        const uploaded = await uploadImageWithValidation({ file })
        setImages((prev) =>
          prev.map((image) => {
            if (image.id !== localId) {
              return image
            }
            if (image.previewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(image.previewUrl)
            }
            return {
              ...image,
              status: 'success',
              url: uploaded.url,
              blobName: uploaded.blobName,
              previewUrl: uploaded.url,
            }
          }),
        )
      } catch (error) {
        const message = getApiErrorText(error) ?? t('poi.media.imageUploadFailed')
        setImages((prev) =>
          prev.map((image) =>
            image.id === localId ? { ...image, status: 'error', error: message } : image,
          ),
        )
      }
    }
  }

  return { handleSelectedImages }
}
